import { Clock, Users, Workflow, FileText, Eye, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { cn } from '@/lib/utils'
import type { IndustryPresetSummary, PresetComplexity } from '@/lib/api/services/industryPresets'

interface PresetCardProps {
  preset: IndustryPresetSummary
  onPreview: (preset: IndustryPresetSummary) => void
  onSelect: (preset: IndustryPresetSummary) => void
  isSelected?: boolean
  className?: string
}

const complexityColors: Record<PresetComplexity, string> = {
  Basic: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
  Standard: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  Advanced: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  Enterprise: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
}

export function PresetCard({
  preset,
  onPreview,
  onSelect,
  isSelected = false,
  className,
}: PresetCardProps) {
  return (
    <Card
      className={cn(
        'group relative overflow-hidden transition-all hover:shadow-lg hover:border-primary/50',
        isSelected && 'ring-2 ring-primary border-primary',
        className
      )}
    >
      {/* Color accent bar */}
      <div
        className="absolute top-0 left-0 right-0 h-1"
        style={{ backgroundColor: preset.color }}
      />

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${preset.color}15` }}
            >
              {getIndustryIcon(preset.icon)}
            </div>
            <div>
              <CardTitle className="text-lg">{preset.name}</CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge
                  variant="secondary"
                  className={cn('text-xs rounded-full', complexityColors[preset.complexity])}
                >
                  {preset.complexity}
                </Badge>
                {preset.isFeatured && (
                  <Badge className="text-xs rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    Featured
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </div>
        <CardDescription className="mt-2 line-clamp-2">{preset.description}</CardDescription>
      </CardHeader>

      <CardContent className="pt-0">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 py-3 border-t border-b border-gray-100 dark:border-gray-800">
          <StatItem icon={<Users className="h-4 w-4" />} value={preset.agentCount} label="Agents" />
          <StatItem icon={<Workflow className="h-4 w-4" />} value={preset.workflowCount} label="Workflows" />
          <StatItem icon={<FileText className="h-4 w-4" />} value={preset.templateCount} label="Templates" />
        </div>

        {/* Setup time and target roles */}
        <div className="flex items-center gap-2 mt-3 text-sm text-muted-foreground">
          <Clock className="h-4 w-4" />
          <span>~{preset.estimatedSetupMinutes} min setup</span>
        </div>

        {preset.targetRoles.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-2">
            {preset.targetRoles.slice(0, 3).map((role) => (
              <span
                key={role}
                className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400"
              >
                {role}
              </span>
            ))}
            {preset.targetRoles.length > 3 && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                +{preset.targetRoles.length - 3} more
              </span>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 rounded-xl"
            onClick={() => onPreview(preset)}
          >
            <Eye className="h-4 w-4 mr-1.5" />
            Preview
          </Button>
          <Button
            size="sm"
            className="flex-1 rounded-xl"
            onClick={() => onSelect(preset)}
            style={{ backgroundColor: preset.color }}
          >
            Select
            <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

function StatItem({ icon, value, label }: { icon: React.ReactNode; value: number; label: string }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1 text-muted-foreground">
        {icon}
        <span className="font-semibold text-foreground">{value}</span>
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  )
}

function getIndustryIcon(iconName: string): string {
  const icons: Record<string, string> = {
    scale: '\u2696\ufe0f',
    'chart-line': '\ud83d\udcc8',
    heartbeat: '\ud83c\udfe5',
    cogs: '\u2699\ufe0f',
    megaphone: '\ud83d\udce2',
    home: '\ud83c\udfe0',
    'graduation-cap': '\ud83c\udf93',
    briefcase: '\ud83d\udcbc',
    industry: '\ud83c\udfed',
    'shopping-cart': '\ud83d\uded2',
    shield: '\ud83d\udee1\ufe0f',
    heart: '\u2764\ufe0f',
    landmark: '\ud83c\udfdb\ufe0f',
    ruler: '\ud83d\udcd0',
    users: '\ud83d\udc65',
  }
  return icons[iconName] || '\ud83d\udcc1'
}

export default PresetCard
